import type React from 'react';

import { Text } from '@change/ds-core-ui/Text';
import { TikTokIcon } from '@change/ds-icons/TikTokIcon';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

/**
 * "Created by <TikTok logo> <name>" pill crediting the social creator whose video the
 * petition was built from.
 *
 * `on-dark` sits over the mobile hero's black media area (translucent overlay fill,
 * 12px type); `on-light` sits above the desktop media area on the page background
 * (raised fill, 14px type, larger avatar).
 */
export const CreatorBadge: React.FC<Props> = ({ name, tone }) => {
	const onDark = tone === 'on-dark';

	if (!name) return null;
	const textSize = onDark ? 'x-small' : 'small';
	const textColor = onDark ? 'text-typography-inverse-strong-fixed' : 'text-typography-strong';

	return (
		<div className="flex items-center gap-2">
			<Text
				variant="body"
				size={textSize}
				fontWeight={onDark ? 'bold' : 'normal'}
				className={cn('whitespace-nowrap', textColor)}
			>
				<Translate value="corgi.petition_details.video_first_hero.created_by" />
			</Text>
			<div
				className={cn('flex min-w-0 items-center gap-1 rounded-full border py-1 pr-3 pl-1', {
					'bg-fill-overlay border-stroke-strong': onDark,
					'bg-background-raised border-stroke-weaker': !onDark,
				})}
			>
				<div
					aria-hidden="true"
					className={cn(
						'bg-fill-strong text-icon-inverse-strong flex shrink-0 items-center justify-center rounded-full shadow-sm',
						onDark ? 'h-6 w-6' : 'border-stroke-inverse-strong h-8 w-8 border',
					)}
					data-qa="video-first-hero-platform"
				>
					<TikTokIcon size={onDark ? 'x-small' : 'small'} />
				</div>
				<Text variant="body" size={textSize} fontWeight="bold" truncate className={cn('min-w-0', textColor)}>
					{name}
				</Text>
			</div>
		</div>
	);
};

type Props = {
	name: string | undefined;
	tone: 'on-dark' | 'on-light';
};
